import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, setStoredSignupIntent } from "../../context/AuthContext";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";

export default function LoginPage() {
  const {
    signIn,
    syncAccount,
    isAuthenticated,
    loading,
    isDevMode,
    emailVerified,
    isPartner,
    isAdmin,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const asPartner = searchParams.get("as") === "partner";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (asPartner) setStoredSignupIntent("partner");
  }, [asPartner]);

  const defaultDest =
    asPartner || isPartner || user?.role === "partner" ? "/partner" : "/instructor";
  const from = location.state?.from || defaultDest;

  const needsPartnerConversion =
    asPartner &&
    isAuthenticated &&
    !loading &&
    !isPartner &&
    !isAdmin &&
    user?.role !== "partner";

  useEffect(() => {
    if (!needsPartnerConversion) return undefined;
    let cancelled = false;
    setConverting(true);
    setError("");
    // #region agent log
    fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'stuck-pre',hypothesisId:'A',location:'LoginPage.jsx:convert-start',message:'Partner conversion started',data:{role:user?.role||null,hasUser:Boolean(user),isPartner,isAdmin},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    syncAccount(user?.display_name || undefined, { intent: "partner" })
      .then((data) => {
        if (cancelled) return;
        const role = data?.user?.role || null;
        const hasPartner = Boolean(data?.partner);
        // #region agent log
        fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'stuck-pre',hypothesisId:'A',location:'LoginPage.jsx:convert-result',message:'Partner conversion sync returned',data:{role,hasPartner,stillNeedsConversion:role!=='partner'&&!hasPartner},timestamp:Date.now()})}).catch(()=>{});
        // #endregion
      })
      .catch((err) => {
        if (cancelled) return;
        // #region agent log
        fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'stuck-pre',hypothesisId:'B',location:'LoginPage.jsx:convert-error',message:'Partner conversion failed',data:{error:String(err?.message||err),status:err?.status||null},timestamp:Date.now()})}).catch(()=>{});
        // #endregion
        setError(err.message || "Could not switch to a partner account");
      })
      .finally(() => {
        if (!cancelled) setConverting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsPartnerConversion, syncAccount, user?.display_name, user?.role]);

  // #region agent log
  useEffect(() => {
    fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'stuck-pre',hypothesisId:'C',location:'LoginPage.jsx:state',message:'Login conversion UI state',data:{asPartner,loading,isAuthenticated,isPartner,role:user?.role||null,needsPartnerConversion,converting,hasError:Boolean(error),stuck:Boolean(needsPartnerConversion&&!converting&&!error)},timestamp:Date.now()})}).catch(()=>{});
  }, [asPartner, loading, isAuthenticated, isPartner, user?.role, needsPartnerConversion, converting, error]);
  // #endregion

  if (!loading && isAuthenticated) {
    if (!isDevMode && !emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }
    if (needsPartnerConversion || converting) {
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'stuck-pre',hypothesisId:'A',location:'LoginPage.jsx:render-switching',message:'Rendering switching screen',data:{needsPartnerConversion,converting,hasError:Boolean(error),role:user?.role||null},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-emerald-50 to-white px-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-gray-600">
              {error
                ? error
                : "Switching your account to partner onboarding…"}
            </p>
            {error && (
              <Link
                to="/become-a-partner"
                className="mt-4 inline-block text-sm font-medium text-emerald-700 underline"
              >
                Back to become a partner
              </Link>
            )}
          </div>
        </div>
      );
    }
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const options = asPartner ? { intent: "partner" } : {};
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'D',location:'LoginPage.jsx:submit',message:'Partner/instructor login submit',data:{asPartner,intent:options.intent||null},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      const data = await signIn(email.trim(), password, options);
      const verified = isDevMode || Boolean(data?.user?.email_verified);
      const roleDest =
        data?.user?.role === "partner" || asPartner ? "/partner" : from;
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'E',location:'LoginPage.jsx:post-signin',message:'Sign-in succeeded, navigating',data:{asPartner,role:data?.user?.role||null,verified,roleDest},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      navigate(verified ? roleDest : "/verify-email", { replace: true });
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'D',location:'LoginPage.jsx:submit-error',message:'Sign-in failed',data:{asPartner,error:String(err?.message||err)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setError(err.message || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-emerald-50 to-white px-4">
      <Seo
        title={asPartner ? "Partner Sign In" : "Instructor Sign In"}
        description={
          asPartner
            ? "Sign in to your Yoga Retreats Booking partner dashboard."
            : "Sign in to your Yoga Retreats Booking instructor dashboard."
        }
        canonicalPath={asPartner ? "/login?as=partner" : "/login"}
        robots="noindex,nofollow"
      />
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <BrandMark />
          <span className="font-bold text-gray-900">
            Yoga Retreats <span className="text-emerald-600">Booking</span>
          </span>
        </Link>
        <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <h1 className="mb-1 text-2xl font-semibold text-gray-900">Sign in</h1>
          <p className="mb-6 text-sm text-gray-500">
            {asPartner ? "Access your partner dashboard" : "Access your instructor dashboard"}
            {isDevMode ? " (dev auth mode)" : ""}
          </p>
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Email</span>
              <input
                type="email"
                required
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <label className="block">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-gray-700">Password</span>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-emerald-700 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                required={!isDevMode}
                minLength={isDevMode ? 0 : 6}
                name="current-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder={isDevMode ? "Any password in dev mode" : ""}
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-emerald-600 py-2.5 font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-gray-600">
            {asPartner ? (
              <>
                New partner?{" "}
                <Link
                  to="/signup?as=partner"
                  className="font-medium text-emerald-700 hover:underline"
                >
                  Create account
                </Link>
              </>
            ) : (
              <>
                New instructor?{" "}
                <Link to="/signup" className="font-medium text-emerald-700 hover:underline">
                  Create account
                </Link>
              </>
            )}
          </p>
          <p className="mt-3 text-center text-xs text-gray-500">
            <Link
              to={asPartner ? "/become-a-partner" : "/become-an-instructor"}
              className="font-medium text-emerald-700 hover:underline"
            >
              {asPartner ? "Become a partner" : "Become an instructor"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
